import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FinanceTabs, FinanceHeader, money, isScopedFinanceUser, ScopedLedgerNotice } from "./financeShell";

const METHOD_LABEL = {
  stripe: "Stripe", wise: "Wise Transfer", paypal: "PayPal",
  thai_bank: "Thai Bank Transfer", thai_qr: "Thai QR",
  wire: "International Wire", ach: "ACH", bank_transfer: "Bank Transfer",
  manual: "Manual",
};

const METHOD_OPTIONS = [
  { value: "wise",         label: "Wise Transfer" },
  { value: "paypal",       label: "PayPal" },
  { value: "thai_bank",    label: "Thai Bank Transfer" },
  { value: "thai_qr",      label: "Thai QR" },
  { value: "wire",         label: "International Wire" },
  { value: "ach",          label: "ACH" },
  { value: "bank_transfer",label: "Bank Transfer" },
  { value: "stripe",       label: "Stripe" },
  { value: "manual",       label: "Manual" },
];

const STATUS_TONE = {
  pending_verification: "bg-amber-100 text-amber-700",
  submitted:            "bg-amber-100 text-amber-700",
  verified:             "bg-emerald-100 text-emerald-700",
  declined:             "bg-rose-100 text-rose-700",
  rejected:             "bg-rose-100 text-rose-700",
};

const ccyFmt = (v, ccy = "USD") => {
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(Number(v || 0)); }
  catch { return `${ccy} ${Number(v || 0).toFixed(2)}`; }
};

// ── Verify & Post Dialog ───────────────────────────────────────────────────────
function VerifyAndPostDialog({ report, onClose, onSuccess }) {
  const expectedAmount = Number(report.invoice_balance ?? report.amount_reported ?? 0);
  const [actualAmount, setActualAmount] = useState(expectedAmount.toFixed(2));
  const [paymentDate, setPaymentDate] = useState(report.payment_date?.slice(0, 10) || "");
  const [refNumber, setRefNumber] = useState(report.reference_number || "");
  const [methodCode, setMethodCode] = useState(report.method_code || "manual");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const actual = parseFloat(actualAmount) || 0;
  const amountDiffers = Math.abs(actual - expectedAmount) > 0.009;
  const noteRequired = amountDiffers && !note.trim();
  const canSubmit = actual > 0 && !noteRequired;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const res = await api.post(`/finance/payment-reports/${report.id}/verify-and-post`, {
        actual_amount: actual,
        payment_date: paymentDate || undefined,
        reference_number: refNumber.trim() || undefined,
        method_code: methodCode,
        note: note.trim() || undefined,
      });
      const status = res.data.invoice_status;
      if (status === "overpayment") {
        toast.warning(
          `Overpayment: ${ccyFmt(res.data.overpayment_amount, report.currency)} excess — invoice is in overpayment state and requires staff resolution.`,
          { duration: 10000 }
        );
      } else {
        toast.success(`Payment posted — invoice is now ${status?.replace(/_/g, " ")}`);
      }
      onSuccess();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || "Failed to post payment. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-end bg-black/40 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={() => !busy && onClose()}
      data-testid="verify-post-dialog"
    >
      <div
        className="relative bg-white h-full w-full max-w-md shadow-2xl border-l border-slate-200 flex flex-col animate-in slide-in-from-right duration-200 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-base font-bold text-slate-900" data-testid="verify-post-title">
              Verify & Post Payment
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">{report.client_name}</p>
            {report.invoice_number && (
              <p className="text-xs text-slate-400 font-mono">{report.invoice_number}</p>
            )}
          </div>
          <button onClick={onClose} disabled={busy}
            className="text-slate-400 hover:text-slate-700 transition-colors disabled:opacity-30 ml-4 mt-0.5"
            data-testid="verify-post-close">
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 px-6 py-5 space-y-5">

          {/* Payment Method */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Payment Method
            </label>
            <select
              value={methodCode}
              onChange={(e) => setMethodCode(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40"
              data-testid="verify-post-method"
            >
              {METHOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Expected amount (read-only context) */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Expected Amount (Invoice Balance)
            </label>
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
              <span className="text-sm font-semibold text-slate-700" data-testid="verify-post-expected">
                {ccyFmt(expectedAmount, report.currency)}
              </span>
              <span className="text-xs text-slate-400">{report.currency}</span>
            </div>
          </div>

          {/* Actual amount received — editable, prefilled */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Actual Amount Received
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                {report.currency || "USD"}
              </span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={actualAmount}
                onChange={(e) => setActualAmount(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded-lg pl-12 pr-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 text-slate-900 font-semibold"
                data-testid="verify-post-actual-amount"
              />
            </div>
            {amountDiffers && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <Icons.AlertCircle className="h-3.5 w-3.5 shrink-0" />
                Amount differs from expected — a verification note is required below.
              </p>
            )}
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Payment Date
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 text-slate-800"
              data-testid="verify-post-payment-date"
            />
          </div>

          {/* Reference / Transaction ID */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Transaction / Reference ID
            </label>
            <input
              type="text"
              value={refNumber}
              onChange={(e) => setRefNumber(e.target.value)}
              placeholder="e.g. TXN-12345 or bank reference"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 text-slate-800 font-mono"
              data-testid="verify-post-reference"
            />
          </div>

          {/* Verification Note */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Verification Note
              {amountDiffers && <span className="text-rose-500 ml-1">*required</span>}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={amountDiffers
                ? "Required: explain the discrepancy between reported and actual amounts."
                : "Optional — add any verification context, audit notes, or confirmation source."}
              className={`w-full text-sm border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 resize-none text-slate-800 ${
                noteRequired ? "border-rose-300 bg-rose-50" : "border-slate-200"}`}
              data-testid="verify-post-note"
            />
          </div>

          {/* Proof document link */}
          {report.proof_document_id && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Icons.FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                <span className="text-sm font-semibold text-emerald-700">Payment Proof Attached</span>
              </div>
              <p className="text-xs text-emerald-600 mb-2">Client uploaded payment confirmation. Review before posting.</p>
              <a
                href={`/api/documents-intel/${report.proof_document_id}/content`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900 border border-emerald-300 bg-white rounded-lg px-3 py-1.5 transition-colors"
                data-testid="verify-post-view-proof">
                <Icons.ExternalLink className="h-3.5 w-3.5" />
                View / Download Proof
              </a>
            </div>
          )}

          {/* THB settlement evidence (cross-currency) */}
          {report.source_amount && report.source_currency === "THB" && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="thb-evidence-section">
              <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Thai Baht Settlement Evidence</div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-xs text-amber-600">Client Sent</div>
                  <div className="font-bold text-amber-900 mt-0.5">฿{Number(report.source_amount).toLocaleString("th-TH")}</div>
                </div>
                <div>
                  <div className="text-xs text-amber-600">FX Rate Used</div>
                  <div className="font-bold text-amber-900 mt-0.5">{Number(report.fx_rate).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs text-amber-600">Rate As Of</div>
                  <div className="font-bold text-amber-900 mt-0.5 text-xs">{(report.rate_as_of || "").slice(0, 10)}</div>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-amber-200 text-xs text-amber-600">
                Post <strong>{ccyFmt(report.invoice_balance, report.currency)}</strong> — the USD invoice balance. Do not post the THB amount.
              </div>
            </div>
          )}

          {/* Fee note */}
          {report.fee_amount > 0 && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
              <Icons.AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                The client paid a <strong>{ccyFmt(report.fee_amount, report.currency)}</strong> provider fee
                in addition to the invoice balance. Post <em>only</em> the Invoice Balance against this invoice.
              </span>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="text-sm px-4 py-2 border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            data-testid="verify-post-cancel">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || busy}
            className="text-sm px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="verify-post-submit">
            {busy
              ? <Icons.Loader2 className="h-4 w-4 animate-spin" />
              : <Icons.CheckCircle2 className="h-4 w-4" />}
            Verify & Post
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Decline Dialog ─────────────────────────────────────────────────────────────
function DeclineDialog({ report, onClose, onSuccess }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const handleDecline = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/finance/payment-reports/${report.id}/decline`, { reason: reason.trim() });
      toast.success("Payment report declined — client notified.");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to decline report. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={() => !busy && onClose()}
      data-testid="decline-dialog"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md mx-4 p-6 animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="h-10 w-10 rounded-xl bg-rose-50 border border-rose-100 flex items-center justify-center">
            <Icons.XCircle className="h-5 w-5 text-rose-600" />
          </div>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-30">
            <Icons.X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-1">Decline payment report?</h3>
        <p className="text-sm text-slate-500 mb-4">
          The invoice will revert to <strong>Issued</strong> so the client can resubmit.
          A portal notification will be sent explaining the decline.
        </p>
        <div className="mb-4">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
            Reason <span className="text-rose-500">*required</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Payment not received / wrong amount transferred / wrong account..."
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-rose-400/40 resize-none text-slate-800"
            data-testid="decline-reason-input"
          />
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={busy}
            className="text-sm px-4 py-2 border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            data-testid="decline-cancel">
            Cancel
          </button>
          <button
            onClick={handleDecline}
            disabled={!reason.trim() || busy}
            className="text-sm px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="decline-submit">
            {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.XCircle className="h-4 w-4" />}
            Decline Report
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PaymentReports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const scoped = isScopedFinanceUser(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [reports, setReports] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDecline, setShowDecline] = useState(null);
  const autoOpenDone = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/finance/payment-reports");
      return r.data.items || [];
    } catch { return []; }
  }, []);

  const refresh = useCallback(() => {
    setReports(null);
    load().then(setReports);
  }, [load]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-open specific report when ?open={id} is in URL (deeplink from Live Ops)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || autoOpenDone.current || reports === null) return;
    const target = reports.find((r) => r.id === openId);
    if (target) {
      autoOpenDone.current = true;
      // Switch filter to pending so the report is visible
      if (["pending_verification", "submitted"].includes(target.status)) {
        setStatusFilter("pending");
      } else {
        setStatusFilter("all");
      }
      setSelectedReport(target);
      // Clean up URL param without re-render loop
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete("open"); return next; }, { replace: true });
    }
  }, [reports, searchParams, setSearchParams]);

  const filtered = (reports || []).filter((r) => {
    if (statusFilter === "pending") return ["pending_verification", "submitted"].includes(r.status);
    if (statusFilter === "all") return true;
    return r.status === statusFilter;
  });

  const pendingCount = (reports || []).filter((r) =>
    ["pending_verification", "submitted"].includes(r.status)).length;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="payment-reports-page">
      <FinanceHeader
        title="Payment Reports"
        subtitle="Client-reported manual payments pending staff verification and posting."
      >
        <button onClick={refresh}
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm border border-slate-200 rounded-lg px-3 py-2"
          data-testid="payment-reports-refresh">
          <Icons.RefreshCw className="h-4 w-4" />Refresh
        </button>
      </FinanceHeader>

      <FinanceTabs user={user} />
      {scoped && <ScopedLedgerNotice teamScope={user?.finance_team_scope} />}

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="payment-reports-filters">
        {[
          ["pending", `Pending${pendingCount > 0 ? ` (${pendingCount})` : ""}`],
          ["all", "All Reports"],
          ["verified", "Verified"],
          ["declined", "Declined"],
        ].map(([val, lbl]) => (
          <button key={val} onClick={() => setStatusFilter(val)}
            className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${
              statusFilter === val
                ? "bg-[#171B2E] text-white border-[#171B2E]"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
            data-testid={`payment-reports-filter-${val}`}>
            {lbl}
          </button>
        ))}
      </div>

      {reports === null ? (
        <div className="flex justify-center py-16">
          <Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center" data-testid="payment-reports-empty">
          <Icons.CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No pending payment reports</p>
          <p className="text-sm text-slate-500 mt-1">All client payment reports have been processed.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-testid="payment-reports-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Client</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Invoice</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Method</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Reported</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Reference</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const tone = STATUS_TONE[r.status] || "bg-slate-100 text-slate-600";
                const invoiceBalance = r.invoice_balance ?? r.amount_reported;
                const isPending = ["pending_verification", "submitted"].includes(r.status);
                return (
                  <tr key={r.id} className={`border-b border-slate-100 hover:bg-slate-50 ${i % 2 === 0 ? "" : "bg-slate-50/30"}`}
                    data-testid={`payment-report-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{r.client_name || "—"}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/finance/invoices/${r.invoice_id}`)}
                        className="font-mono text-xs text-slate-700 hover:text-[#F5A623] transition-colors"
                        data-testid={`payment-report-invoice-link-${r.id}`}>
                        {r.invoice_number || r.invoice_id?.slice(0, 8)}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {METHOD_LABEL[r.method_code] || r.method_code}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold text-slate-900" data-testid={`payment-report-amount-${r.id}`}>
                        {ccyFmt(invoiceBalance, r.currency)}
                      </div>
                      {r.fee_amount > 0 && (
                        <div className="text-xs text-amber-600">+{ccyFmt(r.fee_amount, r.currency)} fee</div>
                      )}
                      {/* THB settlement evidence — shown inline for Thai methods */}
                      {r.source_amount && r.source_currency === "THB" && (
                        <div className="text-xs text-amber-700 mt-0.5" data-testid={`payment-report-thb-${r.id}`}>
                          ฿{Number(r.source_amount).toLocaleString("th-TH")}
                          {r.fx_rate ? ` @ ${Number(r.fx_rate).toFixed(2)}` : ""}
                        </div>
                      )}
                      {r.proof_document_id && (
                        <a href={`/api/documents-intel/${r.proof_document_id}/content`} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1 mt-0.5 justify-end"
                          data-testid={`payment-report-proof-link-${r.id}`}>
                          <Icons.FileText className="h-3 w-3" />Proof
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {r.payment_date || r.created_at?.slice(0, 10) || "—"}
                    </td>
                    <td className="px-4 py-3">
                      {r.reference_number
                        ? <span className="font-mono text-xs text-slate-700">{r.reference_number}</span>
                        : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${tone}`}>
                        {r.status?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {isPending && (
                          <>
                            <button
                              onClick={() => setSelectedReport(r)}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#171B2E] hover:bg-[#F5A623] hover:text-white transition-colors rounded-lg px-3 py-1.5"
                              data-testid={`payment-report-review-${r.id}`}>
                              <Icons.CheckCircle2 className="h-3.5 w-3.5" />
                              Review
                            </button>
                            <button
                              onClick={() => setShowDecline(r)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 hover:text-rose-800 transition-colors"
                              data-testid={`payment-report-decline-${r.id}`}>
                              <Icons.XCircle className="h-3.5 w-3.5" />
                              Decline
                            </button>
                          </>
                        )}
                        {!isPending && (
                          <span className="text-xs text-slate-400">
                            {r.status === "verified"
                              ? `Posted ${r.verified_at?.slice(0, 10) || ""}`
                              : `Declined ${r.declined_at?.slice(0, 10) || ""}`}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Fee accounting note */}
          {filtered.some((r) => r.fee_amount > 0) && (
            <div className="px-4 py-3 bg-amber-50 border-t border-amber-200 text-xs text-amber-700 flex items-start gap-2">
              <Icons.AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                <strong>Fee accounting:</strong> Post only the Invoice Balance against the invoice.
                Provider fees are retained by the payment processor and must not be posted as revenue.
              </span>
            </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      {selectedReport && (
        <VerifyAndPostDialog
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          onSuccess={() => { setSelectedReport(null); autoOpenDone.current = false; refresh(); }}
        />
      )}
      {showDecline && (
        <DeclineDialog
          report={showDecline}
          onClose={() => setShowDecline(null)}
          onSuccess={() => { setShowDecline(null); refresh(); }}
        />
      )}
    </div>
  );
}
